'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/shared/components/ui/button';
import { SigninModal, useCurrentUser } from '@/contexts/authentication/client';
import { ArrowRight, Sparkles } from 'lucide-react';

/**
 * The last invitation. `job` carries the page's own job into the order, so a
 * reader who came for bedtime does not land in a book order (`FIRST.R18`).
 */
export default function CTASection({ job = 'hero' }: { job?: string } = {}) {
  // `gift` starts from a story that already exists, so its door is the shelf.
  const door = (locale: string) => (job === 'gift' ? `/${locale}/create/gift` : `/${locale}/create?job=${job}`);
  const { isAuthenticated } = useCurrentUser();
  const locale = useLocale();
  const t = useTranslations();
  const [isSigninModalOpen, setIsSigninModalOpen] = useState(false);

  return (
    <section data-testid="landing-cta-section" className="relative py-20 lg:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-brand-purple to-brand-pink" />
      <div className="absolute inset-0 bg-black/10" />
      <div className="absolute inset-0">
        <div className="absolute top-16 left-16 w-64 h-64 bg-background/5 rounded-full blur-3xl" />
        <div className="absolute bottom-16 right-16 w-80 h-80 bg-background/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
          {t('cta.title')}
        </h2>

        <p className="text-lg sm:text-xl text-white/85 mb-10 max-w-2xl mx-auto leading-relaxed">
          {t('cta.description')}
        </p>

        {/* The primary action sits on the brand gradient, so it must be a WHITE
            pill with brand-coloured text in both themes. Theme tokens made it a
            near-black button in dark mode, which handed the visual lead to the
            secondary "learn more" link. */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {isAuthenticated ? (
            <Button
              size="lg"
              variant="secondary"
              className="bg-white text-brand-purple hover:bg-white/90 text-lg px-8 shadow-xl"
              asChild
            >
              <Link href={door(locale)}>
                <Sparkles className="h-5 w-5" />
                {t('cta.playNow')}
              </Link>
            </Button>
          ) : (
            <Button
              data-testid="landing-cta-get-started-button"
              size="lg"
              variant="secondary"
              onClick={() => setIsSigninModalOpen(true)}
              className="bg-white text-brand-purple hover:bg-white/90 text-lg px-8 shadow-xl"
            >
              <Sparkles className="h-5 w-5" />
              {t('cta.getStarted')}
            </Button>
          )}

          <Button
            variant="outline-on-color"
            size="lg"
            className="text-lg px-8"
            asChild
          >
            <Link href={`/${locale}/info/about`}>
              {t('cta.learnMore')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>

      <SigninModal
        isOpen={isSigninModalOpen}
        onClose={() => setIsSigninModalOpen(false)}
        callbackUrl={door(locale)}
      />
    </section>
  );
}
